import { z } from "zod";
import type { FieldValues } from "react-hook-form";
import {
  DOCUMENT_TYPES,
  INCOME_SOURCES,
  OCCUPATIONS,
  OPEN_ACCOUNT_REASONS,
} from "../interfaces/kyc.interface";
import {
  isP100SupportedCountryCode,
  P100_SUPPORTED_COUNTRY_ERROR_MESSAGE,
} from "../constants/supported-countries";

const ADDITIONAL_INFO_REQUIRED_INCOME_SOURCES = new Set([
  "OTHER",
  "MAINTAINED_BY_FAMILY",
  "INVESTMENTS",
  "SOCIAL_BENEFITS",
  "TENANCY",
]);

const isoCountryCodeSchema = z
  .string()
  .length(2, "Use the 2-letter ISO code")
  .regex(/^[A-Z]{2}$/, "Only uppercase letters are allowed");

const supportedIsoCountryCodeSchema = isoCountryCodeSchema.refine(
  isP100SupportedCountryCode,
  {
    message: P100_SUPPORTED_COUNTRY_ERROR_MESSAGE,
  }
);

const optionalTrimmedString = (maxLength: number, label: string) =>
  z
    .union([
      z
        .string()
        .trim()
        .min(1, `${label} is required`)
        .max(maxLength, `${label} must be ${maxLength} characters or less`),
      z.literal(""),
      z.undefined(),
    ])
    .transform((value) => {
      if (!value) return undefined;
      const trimmed = value.trim();
      return trimmed === "" ? undefined : trimmed;
    });

export const setupKycSchema = z
  .object({
    documentType: z.enum(DOCUMENT_TYPES, {
      message: "Select a document type",
    }),
    country: supportedIsoCountryCodeSchema,
    incomeSource: z.enum(INCOME_SOURCES, {
      message: "Select an income source",
    }),
    openAccountReason: z.enum(OPEN_ACCOUNT_REASONS, {
      message: "Select a reason",
    }),
    phone: z
      .string()
      .min(6, "Phone number is required")
      .max(32, "Phone number is too long")
      .regex(/^[0-9+()\- ]+$/, "Enter a valid phone number")
      .transform((value) => value.replace(/[\s()-]/g, "")),
    street: z.string().min(1, "Street is required"),
    number: z.string().min(1, "Number is required"),
    city: z.string().min(1, "City is required"),
    postCode: z.string().min(1, "Post code is required"),
    citizenship: supportedIsoCountryCodeSchema,
    nationality: supportedIsoCountryCodeSchema,
    birthCountry: supportedIsoCountryCodeSchema,
    birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD format"),
    isPep: z.boolean(),
    isFatca: z.boolean(),
    taxId: optionalTrimmedString(100, "Tax ID"),
    additionalInfo: z
      .union([z.string(), z.literal(""), z.undefined()])
      .transform((value) => {
        if (typeof value !== "string") return undefined;
        const trimmed = value.trim();
        return trimmed === "" ? undefined : trimmed;
      }),
    occupation: z
      .union([z.enum(OCCUPATIONS), z.literal(""), z.undefined()])
      .transform((value) => (value ? value : undefined)),
  })
  .superRefine((values, ctx) => {
    if (values.incomeSource === "BUSINESS_ACTIVITY") {
      if (!values.taxId) {
        ctx.addIssue({
          path: ["taxId"],
          code: z.ZodIssueCode.custom,
          message: "Tax ID is required for business activity income.",
        });
      }
    } else if (values.taxId) {
      ctx.addIssue({
        path: ["taxId"],
        code: z.ZodIssueCode.custom,
        message: "Tax ID can only be provided for business activity income.",
      });
    }

    if (values.incomeSource === "SALARY") {
      if (!values.occupation) {
        ctx.addIssue({
          path: ["occupation"],
          code: z.ZodIssueCode.custom,
          message: "Occupation is required for salary income.",
        });
      }
    } else if (values.occupation) {
      ctx.addIssue({
        path: ["occupation"],
        code: z.ZodIssueCode.custom,
        message: "Occupation can only be provided for salary income.",
      });
    }

    if (ADDITIONAL_INFO_REQUIRED_INCOME_SOURCES.has(values.incomeSource)) {
      if (!values.additionalInfo) {
        ctx.addIssue({
          path: ["additionalInfo"],
          code: z.ZodIssueCode.custom,
          message:
            "Additional info is required for the selected income source.",
        });
      }
    } else if (values.additionalInfo) {
      ctx.addIssue({
        path: ["additionalInfo"],
        code: z.ZodIssueCode.custom,
        message:
          "Additional info can only be provided for the selected income source.",
      });
    }
  });

export type SetupKycFormValues = z.input<typeof setupKycSchema> & FieldValues;
